import React from 'react';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { SITE_REGEN_KIND } from '../art-style/art-style-api';
import {
  fetchNewLook,
  fetchNewLookFormats,
  gainedRedrawn,
  NO_NEW_LOOK,
  redrawnKey,
  type NewLook,
  type NewLookPhase,
} from './new-look-api';

/** How often a new look still being drawn is read again when no job frame moved it meanwhile. */
export const NEW_LOOK_POLL_MS = 60_000;

/** The brand's site jobs as the jobs feed last streamed them (id, status, parts done), as one string: a frame that
 *  moves one changes it. '' when the feed has none. */
export const siteRegenSignal = (jobs: Record<string, CreativeJob>, tenantId: number | undefined): string =>
  tenantId === undefined
    ? ''
    : Object.values(jobs)
        .filter((j) => j.kind === SITE_REGEN_KIND && String(j.tenant_id) === String(tenantId))
        .map((j) => `${j.id}:${j.status}:${j.completed ?? 0}`)
        .sort()
        .join('|');

/**
 * The brand's new look (see `./new-look-api`): read for the brand, again whenever its site job moves in the jobs
 * feed (a frame per part drawn), and every {@link NEW_LOOK_POLL_MS} while it is drawing in case a frame is lost. A
 * failed read keeps what was shown; reads landing out of order keep the newest asked for.
 */
export const useNewLook = (tenantId: number | undefined): NewLook => {
  const jobs = useAppSelector(selectCreativeJobsMap);
  const signal = siteRegenSignal(jobs, tenantId);
  const [held, setHeld] = React.useState<{ tenant: number; look: NewLook } | null>(null);
  const ticketRef = React.useRef(0);

  const read = React.useCallback((tenant: number, abort?: AbortSignal): void => {
    ticketRef.current += 1;
    const ticket = ticketRef.current;
    void fetchNewLook(tenant, abort).then((look) => {
      if (look && ticketRef.current === ticket) setHeld({ tenant, look });
    });
  }, []);

  // Syncs with spark-creatives: on the brand, and on each move of its site job (`signal`).
  React.useEffect(() => {
    if (tenantId === undefined) return undefined;
    const ctl = new AbortController();
    read(tenantId, ctl.signal);
    return () => ctl.abort();
  }, [tenantId, signal, read]);

  const look = held && held.tenant === tenantId ? held.look : NO_NEW_LOOK;

  // The fallback read while drawing.
  React.useEffect(() => {
    if (tenantId === undefined || look.phase !== 'drawing') return undefined;
    const timer = window.setInterval(() => read(tenantId), NEW_LOOK_POLL_MS);
    return () => window.clearInterval(timer);
  }, [tenantId, look.phase, read]);

  return look;
};

/**
 * Calls `onRedrawn` when the new look reports a banner newly redrawn by a regeneration in place: its live art changed,
 * so the page reads its banners again. What a regeneration had redrawn when it is first read is not new (the page's
 * own read of its banners shows it), so the first look of each regeneration only sets the baseline.
 */
export const useOnRedrawn = (look: NewLook, onRedrawn: () => void): void => {
  const key = redrawnKey(look);
  const seenRef = React.useRef({ jobId: '', key: '' });
  const onRedrawnRef = React.useRef(onRedrawn);
  onRedrawnRef.current = onRedrawn;

  // Syncs the page's banners with spark-creatives: each newly redrawn banner reads them again.
  React.useEffect(() => {
    const prev = seenRef.current;
    seenRef.current = { jobId: look.jobId, key };
    if (prev.jobId === look.jobId && gainedRedrawn(prev.key, key)) onRedrawnRef.current();
  }, [look.jobId, key]);
};

export interface NewLookFormats {
  /** The banner's new art by format slot, once read. */
  formats: Record<string, string> | null;
  loading: boolean;
  failed: boolean;
}

/** One banner's new art, read once it is ready (again for a later regeneration, `jobId`). */
export const useNewLookFormats = (
  tenantId: number | undefined,
  bannerId: string | undefined,
  status: NewLookPhase | undefined,
  jobId: string,
): NewLookFormats => {
  const key = tenantId !== undefined && bannerId && status === 'ready' ? `${tenantId}|${bannerId}|${jobId}` : '';
  const [held, setHeld] = React.useState<{ key: string; formats: Record<string, string> | null } | null>(null);

  // Syncs with spark-creatives: one read per banner and regeneration once its art is ready.
  React.useEffect(() => {
    if (!key || tenantId === undefined || !bannerId) return undefined;
    const ctl = new AbortController();
    void fetchNewLookFormats(tenantId, bannerId, ctl.signal).then((formats) => {
      if (!ctl.signal.aborted) setHeld({ key, formats });
    });
    return () => ctl.abort();
  }, [key, tenantId, bannerId]);

  const mine = held && held.key === key ? held : null;
  return {
    formats: mine?.formats ?? null,
    loading: key !== '' && mine === null,
    failed: mine !== null && mine.formats === null,
  };
};
